import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const OccupancyCostRatio = () => {
  // State variables for Occupancy Cost Ratio calculation
  const [occupancyCost, setOccupancyCost] = useState(0); // Total occupancy cost
  const [salesRevenue, setSalesRevenue] = useState(0); // Sales revenue
  const [costUnit, setCostUnit] = useState("USD"); // Unit for costs
  const [ratio, setRatio] = useState(0); // Calculated Occupancy Cost Ratio

  // Event handlers for input changes
  const handleOccupancyCostChange = (e) => setOccupancyCost(parseFloat(e.target.value) || 0);
  const handleSalesRevenueChange = (e) => setSalesRevenue(parseFloat(e.target.value) || 0);
  const handleCostUnitChange = (e) => setCostUnit(e.target.value);

  // useEffect hook to calculate Occupancy Cost Ratio
  useEffect(() => {
    if (salesRevenue > 0) {
      const calculatedRatio = (occupancyCost / salesRevenue) * 100;
      setRatio(calculatedRatio);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [occupancyCost, salesRevenue]); // Recalculates when occupancy cost or sales revenue changes

  return (
    <Layout title="Occupancy Cost Ratio Calculator" path="occupancy-cost-ratio">
      <section className="section-padding" id="occupancy-cost-ratio">
        <div className="container mt-5">
          <div className="row">
            {/* Occupancy Cost Ratio Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Occupancy Cost Ratio Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Total Occupancy Cost */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="occupancyCost" className="form-label">
                        Total Occupancy Cost
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Occupancy Cost:</strong> The total cost incurred for occupying a space, including rent, utilities, and other related expenses."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="occupancyCost"
                        placeholder="Enter total occupancy cost"
                        value={occupancyCost || ""}
                        onChange={handleOccupancyCostChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={costUnit}
                        onChange={handleCostUnitChange}
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="INR">INR</option>
                        <option value="JPY">JPY</option>
                        <option value="AUD">AUD</option>
                        <option value="CAD">CAD</option>
                        <option value="CHF">CHF</option>
                        <option value="CNY">CNY</option>
                        <option value="SGD">SGD</option>
                      </select>
                    </div>

                    {/* Input for Sales Revenue */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="salesRevenue" className="form-label">
                        Sales Revenue
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Sales Revenue:</strong> The total revenue generated from the sales of products or services, before accounting for expenses."
                    style={{ cursor: "pointer" }}
                  > 
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="salesRevenue"
                        placeholder="Enter sales revenue"
                        value={salesRevenue || ""}
                        onChange={handleSalesRevenueChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Occupancy Cost Ratio:{" "}
                        <span className="fw-bold ms-3">
                          {ratio === 0
                            ? "0%"
                            : `${ratio.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}%`}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Occupancy Cost Ratio Calculation
                </h3>

                {/* What is Occupancy Cost Ratio? */}
                <h4 className="mt-3 mb-3">What is Occupancy Cost Ratio?</h4>
                <p>
                  **Occupancy Cost Ratio (OCR)** is a financial metric that measures the proportion of a retailer's sales revenue used to cover the costs of renting or owning their retail space.
                </p>

                {/* Why Use the Occupancy Cost Ratio Calculator? */}
                <h4 className="mt-4 mb-3">Why Use the Occupancy Cost Ratio Calculator?</h4>
                <p>
                  This calculator helps businesses assess whether their retail space costs are sustainable based on their revenue. A high ratio may indicate inefficiencies or over-expensive leases.
                </p>

                {/* Benefits of Using the Occupancy Cost Ratio Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using the Occupancy Cost Ratio Calculator</h4>
                <ul>
                  <li>
                    <strong>Supports Feasibility Analysis:</strong> Determines whether a retail space is financially viable.
                  </li>
                  <li>
                    <strong>Improves Cost Management:</strong> Helps identify areas to reduce occupancy costs.
                  </li>
                  <li>
                    <strong>Optimizes Space Planning:</strong> Encourages efficient use of retail space.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use the Occupancy Cost Ratio Calculator?</h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Shopping Malls:</strong> To evaluate the affordability of tenant rents.
                  </li>
                  <li>
                    <strong>Retailers:</strong> To monitor lease expenses relative to sales.
                  </li>
                  <li>
                    <strong>Leasing Managers:</strong> To adjust pricing based on tenant performance.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h5 className="mt-3 mb-2">What is a good occupancy cost ratio?</h5>
                <p>
                  A good occupancy cost ratio typically ranges between 5% to 15%, depending on the industry and location. Ratios above this may indicate excessive space costs.
                </p>

                <h5 className="mt-3 mb-2">How can retailers lower their occupancy cost ratio?</h5>
                <p>
                  Retailers can lower their ratio by increasing sales revenue, renegotiating lease terms, or optimizing space usage to reduce costs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default OccupancyCostRatio;
